import { Alert, Badge, Button, Card, CardBody, Spinner } from '@foundation/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import styles from './cancellations.$id.module.css'
import { api, type Cancellation, type CancellationStatus } from '@/lib/api'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/cancellations/$id')({
  component: CancellationDetailPage,
})

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount / 100)
}

function getStatusBadge(status: CancellationStatus) {
  switch (status) {
    case 'won_back':
      return <Badge variant="success">Recovered</Badge>
    case 'lost':
      return <Badge variant="danger">Lost</Badge>
    case 'active':
      return <Badge variant="primary">In Progress</Badge>
    case 'paused':
      return <Badge variant="warning">Paused</Badge>
    default:
      return <Badge variant="default">{status}</Badge>
  }
}

function CancellationDetailPage() {
  const { id } = Route.useParams()
  const [cancellation, setCancellation] = useState<Cancellation | null>(null)
  const [daysSinceCancellation, setDaysSinceCancellation] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .getCancellation(id)
      .then((res) => {
        setCancellation(res.data)
        setDaysSinceCancellation(
          Math.floor(
            (Date.now() - new Date(res.data.cancelledAt).getTime()) / (1000 * 60 * 60 * 24)
          )
        )
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load cancellation')
      })
      .finally(() => setIsLoading(false))
  }, [id])

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !cancellation) {
    return (
      <div>
        <Link to="/cancellations" className={styles.backLink}>
          &larr; Back to Cancellations
        </Link>
        <Alert variant="danger" style={{ marginTop: '1rem' }}>
          {error ?? 'Cancellation not found'}
        </Alert>
      </div>
    )
  }

  return (
    <>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div>
          <Link to="/cancellations" className={styles.backLink}>
            &larr; Back to Cancellations
          </Link>
          <h1 className={styles.customerTitle}>
            {cancellation.customerName ?? 'Unknown Customer'} {getStatusBadge(cancellation.status)}
          </h1>
          <p className={styles.customerEmail}>{cancellation.customerEmail}</p>
        </div>
        <div className={styles.headerActions}>
          {cancellation.status === 'active' && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => toast.info('Manual recovery is handled via Stripe')}
            >
              Mark as Recovered
            </Button>
          )}
        </div>
      </div>

      {/* Info Cards */}
      <div className={styles.infoGrid}>
        <Card className={styles.infoCard}>
          <CardBody>
            <div className={`${styles.infoValue} ${styles.infoValueDanger}`}>
              {formatCurrency(cancellation.mrr, cancellation.currency)}
            </div>
            <div className={styles.infoLabel}>MRR Lost</div>
          </CardBody>
        </Card>
        <Card className={styles.infoCard}>
          <CardBody>
            <div className={styles.infoValue}>{cancellation.productName ?? '--'}</div>
            <div className={styles.infoLabel}>Plan</div>
          </CardBody>
        </Card>
        <Card className={styles.infoCard}>
          <CardBody>
            <div className={styles.infoValue}>
              {new Date(cancellation.cancelledAt).toLocaleDateString()}
            </div>
            <div className={styles.infoLabel}>Cancellation Date</div>
          </CardBody>
        </Card>
        <Card className={styles.infoCard}>
          <CardBody>
            <div className={styles.infoValue}>{daysSinceCancellation}d</div>
            <div className={styles.infoLabel}>Days Since Cancelled</div>
          </CardBody>
        </Card>
      </div>

      {/* Customer Details */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Customer Details</h3>
        <Card>
          <CardBody>
            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Name</span>
                <span className={styles.detailValue}>
                  {cancellation.customerName ?? 'Not provided'}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Email</span>
                <span className={styles.detailValue}>{cancellation.customerEmail}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Product</span>
                <span className={styles.detailValue}>
                  {cancellation.productName ?? 'Not specified'}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Currency</span>
                <span className={styles.detailValue}>{cancellation.currency.toUpperCase()}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Cancelled At</span>
                <span className={styles.detailValue}>
                  {new Date(cancellation.cancelledAt).toLocaleString()}
                </span>
              </div>
              {cancellation.wonBackAt && (
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Won Back At</span>
                  <span className={styles.detailValue}>
                    {new Date(cancellation.wonBackAt).toLocaleString()}
                  </span>
                </div>
              )}
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Status</span>
                <span className={styles.detailValue}>{getStatusBadge(cancellation.status)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Record Created</span>
                <span className={styles.detailValue}>
                  {new Date(cancellation.createdAt).toLocaleString()}
                </span>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Survey Response */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Survey Response</h3>
        <Card>
          <CardBody>
            {cancellation.surveyReason ? (
              <div>
                <div className={styles.surveyReason}>Reason: {cancellation.surveyReason}</div>
                {cancellation.surveyFreeformText && (
                  <div className={styles.surveyFreeform}>
                    &ldquo;{cancellation.surveyFreeformText}&rdquo;
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.emptySection}>
                No survey response was collected for this cancellation.
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Win-back Timeline */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Win-Back Email Timeline</h3>
        <Card>
          <CardBody>
            <div className={styles.emptySection}>
              Email delivery tracking will appear here once the win-back sequence is triggered for
              this customer. Check the sequence associated with this cancellation for step details.
            </div>
          </CardBody>
        </Card>
      </div>
    </>
  )
}
